import React, { useState } from 'react';

interface OsBrowserPopupProps {
  recordId: string; // The record ID passed to the component
  handleClose: () => void; // Function to close the popup
  onUpdate: (updatedRecord: any) => void; // Function to refresh the data after update
}

interface FormState {
  mobileDevices: { [key: string]: [number, number] };
  mobileBrowsers: { [key: string]: [number, number] };
  desktopOS: { [key: string]: [number, number] };
  desktopBrowsers: { [key: string]: [number, number] };
}

const OsBrowserPopup: React.FC<OsBrowserPopupProps> = ({
  recordId,
  handleClose,
  onUpdate,
}) => {
  const [formState, setFormState] = useState<FormState>({
    mobileDevices: {},
    mobileBrowsers: {},
    desktopOS: {},
    desktopBrowsers: {},
  });

  const handleCheckboxChange = (category: keyof FormState, value: string) => {
    setFormState((prev) => {
      const updatedCategory = prev[category];
      if (updatedCategory[value]) {
        delete updatedCategory[value];
      } else {
        updatedCategory[value] = [0, 150];
      }
      return { ...prev, [category]: updatedCategory };
    });
  };

  const handleRangeChange = (
    category: keyof FormState,
    key: string,
    range: [number, number]
  ) => {
    setFormState((prev) => ({
      ...prev,
      [category]: {
        ...(prev[category] as { [key: string]: [number, number] }),
        [key]: range,
      },
    }));
  };

  const handleSave = () => {
    const updatedRecord = {
      recordId,
      ...formState,
    };
    onUpdate(updatedRecord);
    handleClose();
  };

  return (
    <div className="popup-container" style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '14px' }}>
      <div className="popup-header" style={{ marginBottom: '20px' }}>
        <h4 style={{ fontSize: '16px', margin: '0 0 10px 0' }}>Campaign: OS & Browser Targeting</h4>
      </div>
      <div className="popup-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Mobile Devices */}
        <div>
          <h5 style={{ fontSize: '14px' }}>Mobile Device:</h5>
          {['Android', 'Android Tablet', 'iPhone', 'iPad'].map((os) => (
            <div key={os}>
              <label style={{ color: '#81D34A' }}>
                <input
                  type="checkbox"
                  onChange={() => handleCheckboxChange('mobileDevices', os)}
                  checked={!!formState.mobileDevices[os]}
                />
                {os}
              </label>
              <div style={{ padding: "20px", backgroundColor: "#FAFAFA", marginLeft: '20px', marginTop: '5px' }}>
                <input
                  type="range"
                  min={0}
                  max={20}
                  step={1}
                  value={formState.mobileDevices[os]?.[0] || 0}
                  onChange={(e) =>
                    handleRangeChange('mobileDevices', os, [Number(e.target.value), formState.mobileDevices[os]?.[1] || 20])
                  }
                  style={{ accentColor: '#81D34A' }}
                />
                <input
                  type="range"
                  min={0}
                  max={20}
                  step={1}
                  value={formState.mobileDevices[os]?.[1] || 20}
                  onChange={(e) =>
                    handleRangeChange('mobileDevices', os, [formState.mobileDevices[os]?.[0] || 0, Number(e.target.value)])
                  }
                  style={{ accentColor: '#81D34A' }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Desktop OS */}
        <div>
          <h5 style={{ fontSize: '14px' }}>Desktop OS:</h5>
          {['Windows', 'Macintosh'].map((os) => (
            <div key={os}>
              <label style={{ color: '#81D34A' }}>
                <input
                  type="checkbox"
                  onChange={() => handleCheckboxChange('desktopOS', os)}
                  checked={!!formState.desktopOS[os]}
                />
                {os}
              </label>
              <div style={{ padding: "20px", backgroundColor: "#FAFAFA", marginLeft: '20px', marginTop: '5px' }}>
                <input
                  type="range"
                  min={0}
                  max={20}
                  step={1}
                  value={formState.desktopOS[os]?.[0] || 0}
                  onChange={(e) =>
                    handleRangeChange('desktopOS', os, [Number(e.target.value), formState.desktopOS[os]?.[1] || 20])
                  }
                  style={{ accentColor: '#81D34A' }}
                />
                <input
                  type="range"
                  min={0}
                  max={20}
                  step={1}
                  value={formState.desktopOS[os]?.[1] || 20}
                  onChange={(e) =>
                    handleRangeChange('desktopOS', os, [formState.desktopOS[os]?.[0] || 0, Number(e.target.value)])
                  }
                  style={{ accentColor: '#81D34A' }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Browsers */}
        <div>
          <h5 style={{ fontSize: '14px' }}>Mobile Browsers:</h5>
          {['Chrome', 'FireFox', 'Safari', 'Edge', 'Edge Chromium', 'Samsung Browser'].map((browser) => (
            <div key={browser}>
              <label style={{ color: '#81D34A' }}>
                <input
                  type="checkbox"
                  onChange={() => handleCheckboxChange('mobileBrowsers', browser)}
                  checked={!!formState.mobileBrowsers[browser]}
                />
                {browser}
              </label>
              <div style={{ padding: "20px", backgroundColor: "#FAFAFA", marginLeft: '20px', marginTop: '5px' }}>
                <input
                  type="range"
                  min={0}
                  max={150}
                  step={1}
                  value={formState.mobileBrowsers[browser]?.[0] || 0}
                  onChange={(e) =>
                    handleRangeChange('mobileBrowsers', browser, [Number(e.target.value), formState.mobileBrowsers[browser]?.[1] || 150])
                  }
                  style={{ accentColor: '#81D34A' }}
                />
                <input
                  type="range"
                  min={0}
                  max={150}
                  step={1}
                  value={formState.mobileBrowsers[browser]?.[1] || 150}
                  onChange={(e) =>
                    handleRangeChange('mobileBrowsers', browser, [formState.mobileBrowsers[browser]?.[0] || 0, Number(e.target.value)])
                  }
                  style={{ accentColor: '#81D34A' }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Browsers */}
        <div>
          <h5 style={{ fontSize: '14px' }}>Desktop Browsers:</h5>
          {['Chrome', 'FireFox', 'Safari', 'Edge', 'Edge Chromium'].map((browser) => (
            <div key={browser}>
              <label style={{ color: '#81D34A' }}>
                <input
                  type="checkbox"
                  onChange={() => handleCheckboxChange('desktopBrowsers', browser)}
                  checked={!!formState.desktopBrowsers[browser]}
                />
                {browser}
              </label>
              <div style={{ padding: "20px", backgroundColor: "#FAFAFA", marginLeft: '20px', marginTop: '5px' }}>
                <input
                  type="range"
                  min={0}
                  max={150}
                  step={1}
                  value={formState.desktopBrowsers[browser]?.[0] || 0}
                  onChange={(e) =>
                    handleRangeChange('desktopBrowsers', browser, [Number(e.target.value), formState.desktopBrowsers[browser]?.[1] || 150])
                  }
                  style={{ accentColor: '#81D34A' }}
                />
                <input
                  type="range"
                  min={0}
                  max={150}
                  step={1}
                  value={formState.desktopBrowsers[browser]?.[1] || 150}
                  onChange={(e) =>
                    handleRangeChange('desktopBrowsers', browser, [formState.desktopBrowsers[browser]?.[0] || 0, Number(e.target.value)])
                  }
                  style={{ accentColor: '#81D34A' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="popup-actions" style={{ marginTop: '20px', textAlign: 'center' }}>
        <button
          onClick={handleSave}
          style={{
            padding: '8px 40px',
            backgroundColor: '#1173B7',
            color: 'white',
            fontSize: '14px',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Update
        </button>
      </div>
    </div>
  );
};

export default OsBrowserPopup;
